import React, {useCallback, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ArrowRight, ArrowsClockwise, CaretDown, CaretRight, Check, Lightning, Plugs, Warning} from '../../icons';
import {BOOSTER_ANCHOR_PROMPTS, BOOSTER_STEP_FEED, boosterPathFor} from './boosterSteps';
import {PromptMiniCard, PromptOwnRow, PromptPanelCard, splitBandLibrary, textNeedsClient} from './use_ia/PromptPanelCard';
import {FILTER_ALL, PromptFilters, filterPrompts} from './use_ia/PromptFilters';
import {UploadPromptForm} from './use_ia/UploadPromptForm';
import {ING_MARGEN, LIBRARY_FAILED, connectPathFor, useClientPromptLibrary} from './use_ia/useClientPromptLibrary';
import {splitProfitability} from './use_ia/profitabilityPrompts';
import {useStableCollapse} from './use_ia/useStableCollapse';
import {hasUnresolvablePlaceholders, resolvePromptPlaceholders}
    from '../panel_v11/panelFormatters';
import {useUserModules} from '../../../context/UserModulesContext';

// La pantalla de la agencia donde se crean, editan y desactivan los prompts. La banda es para
// USARLOS; gestionarlos se hace allá.
export const AGENCY_PROMPTS_PATH = '/agency/prompts';

/**
 * La biblioteca de prompts DENTRO de la banda: los de la agencia en filas, los de Dashbo plegados
 * con sus filtros, y al pie subir uno propio o ir a gestionarlos. El sugerido NO está acá: va
 * arriba, en su tarjeta, y sale de las dos listas (`splitBandLibrary`).
 *
 * Lo de la agencia va arriba y a la vista porque es lo que el equipo le pide a su IA por nombre;
 * Dashbo va plegado porque para una agencia con prompts propios sus ~50 tarjetas son el ruido de
 * abajo. **Abre desplegado si la agencia todavía no tiene ninguno**: para una agencia nueva el
 * catálogo es lo único que hay, y plegarlo le dejaría la biblioteca vacía a la vista.
 *
 * Vive acá y no en su propia pantalla porque toda su data ya está en el payload que la banda pide:
 * filtrar, rankear y contar no cuesta un solo request más. El `id` es el ancla `#prompts`, que es
 * el destino al que redirige la ruta vieja `/client/:id/ia/prompts` (`boosterSteps.js`).
 *
 * ⚠️ **La sección se dibuja SIEMPRE; lo que es condicional es su contenido.** El ancla tiene que
 * existir desde el primer paint, igual que `#bitacora`, que por eso vive fuera del panel diferido:
 * `prompts` arranca en `[]` y lo llena un fetch, y `useHashScroll` sale a buscar el elemento a los
 * ~30ms. Con el `return null` afuera, `/client/:id/ia/prompts` —ruta viva que redirige a
 * `#prompts`— aterrizaba al usuario arriba de todo, en Alimenta, sin nada que explicara por qué; y
 * con la biblioteca vacía el ancla no existía nunca. La sección vacía no ocupa lugar
 * (`.iab-lib:empty` le saca el margen).
 */
function UseIaLibrary({clientId, clientName, library, total, status, agencyName, onPromptCreated, collapse}) {
    const {t} = useTranslation();
    const {own, dashbo, hasOwn} = library;
    const [momento, setMomento] = useState(FILTER_ALL);
    const [role, setRole] = useState(FILTER_ALL);
    // `null` = el usuario no lo tocó, y manda el default. Es DERIVADO y no un `useState(!hasOwn)`
    // porque los prompts llegan por fetch: al montar la lista está vacía y el default saldría
    // siempre «abierto».
    const [dashboChoice, setDashboChoice] = useState(null);
    const dashboOpen = dashboChoice === null ? !hasOwn : dashboChoice;

    const visible = useMemo(() => filterPrompts(dashbo, {momento, role}), [dashbo, momento, role]);

    // Plegar achica el final de la página: sin la reserva, el rótulo bajaba hasta 544 px bajo el
    // mouse (`useStableCollapse`). Desplegar no la necesita: vuelve el contenido que la justificaba.
    const toggleDashbo = () => {
        if (dashboOpen) collapse.markBeforeCollapse();
        else collapse.clearReserve();
        setDashboChoice(!dashboOpen);
    };

    const resetFilters = () => {
        setMomento(FILTER_ALL);
        setRole(FILTER_ALL);
    };

    // Subir el PRIMER prompt propio cambia el default (ya hay propios → Dashbo plegado), y el
    // plegado está ARRIBA del form: se cerraría la grilla entera y la página saltaría bajo la mano
    // de quien acaba de guardar. Se congela lo que se estaba viendo; plegarlo es decisión suya.
    const handleCreated = useCallback((prompt) => {
        setDashboChoice((choice) => (choice === null ? !hasOwn : choice));
        if (onPromptCreated) onPromptCreated(prompt);
    }, [hasOwn, onPromptCreated]);

    const client = clientName || t('ia_booster.this_client');
    // El nombre llega del chrome, cacheado; el fallback cubre el primer login, antes de que conteste.
    const agency = (agencyName || '').trim() || t('ia_booster.band.band_agency_fallback');

    return (
        <section className="iab-lib iab-rv" id={BOOSTER_ANCHOR_PROMPTS}>
            {/* Si la biblioteca NO se pudo traer, la sección lo dice en vez de autocolapsarse. Con
                el ancla vacía, quien entra por `#prompts` aterriza en la nada y no puede distinguir
                «falló» de «no hay prompts» — y acá es siempre lo primero: el catálogo es GLOBAL y
                toda agencia recibe el suyo, así que una lista vacía con el fetch OK no existe en
                producción. Mientras carga no se afirma nada, que es lo que ya hacía. */}
            {total === 0 && status === LIBRARY_FAILED && (
                <>
                    <span className="iab-lib-kicker">{t('ia_booster.band.library_kicker')}</span>
                    <h3 className="iab-lib-title">{t('ia_booster.band.library_title')}</h3>
                    <p className="iab-lib-empty">{t('ia_booster.band.library_failed')}</p>
                </>
            )}
            {total > 0 && (
                <>
                    <span className="iab-lib-kicker">{t('ia_booster.band.library_kicker')}</span>
                    <h3 className="iab-lib-title">{t('ia_booster.band.library_title')}</h3>
                    <p className="iab-lib-sub">{t('ia_booster.band.library_sub')}</p>

                    {/* El número es el de las filas que siguen: el sugerido ya está a la vista
                        arriba y no se repite acá. */}
                    {own.length > 0 && (
                        <div className="iab-own">
                            {/* Espacios duros alrededor del «·»: el número nunca queda solo en un
                                renglón (a 390, con un nombre de cliente largo, pasaba). */}
                            <h4 className="iab-own-head">
                                {t('ia_booster.band.band_own_group', {client, interpolation: {escapeValue: false}})}
                                <span className="iab-own-n">{' · '}{own.length}</span>
                            </h4>
                            <ul className="iab-own-rows">
                                {own.map((prompt) => (
                                    <PromptOwnRow key={prompt.id} prompt={prompt} clientId={clientId}
                                                  clientName={clientName}/>
                                ))}
                            </ul>
                        </div>
                    )}

                    {dashbo.length > 0 && (
                        <div className={`iab-dashbo ${dashboOpen ? '-open' : ''}`}>
                            {/* `aria-controls` sólo con el panel montado: plegado no existe, y un
                                id que no resuelve es una referencia rota para el lector de pantalla. */}
                            <button type="button" className="iab-dashbo-toggle" aria-expanded={dashboOpen}
                                    aria-controls={dashboOpen ? 'iab-dashbo-list' : undefined}
                                    ref={collapse.anchorRef} onClick={toggleDashbo}>
                                {dashboOpen ? <CaretDown size={13} weight="bold"/> : <CaretRight size={13} weight="bold"/>}
                                {t('ia_booster.band.band_dashbo_collapsed', {
                                    client, count: dashbo.length, interpolation: {escapeValue: false},
                                })}
                            </button>
                            {dashboOpen && (
                                <div className="iab-dashbo-list" id="iab-dashbo-list">
                                    <PromptFilters prompts={dashbo} momento={momento} role={role}
                                                   onMomentoChange={setMomento} onRoleChange={setRole}/>
                                    {/* El vacío de la combinación trae su salida: volver los dos ejes a
                                        «Todos» sin tener que adivinar cuál apagar. */}
                                    {visible.length === 0 ? (
                                        <p className="iab-lib-empty">
                                            {t('ia_booster.band.library_empty')}{' '}
                                            <button type="button" className="iab-link" onClick={resetFilters}>
                                                {t('ia_booster.band.band_filters_reset')}
                                            </button>
                                        </p>
                                    ) : (
                                        <div className="iab-jgrid">
                                            {visible.map((prompt) => (
                                                <PromptMiniCard key={prompt.id} prompt={prompt} clientId={clientId}/>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="iab-lib-foot">
                        <UploadPromptForm clientId={clientId} onCreated={handleCreated}/>
                        <Link className="iab-manage-link" to={AGENCY_PROMPTS_PATH}>
                            {t('ia_booster.band.band_manage_link', {agency, interpolation: {escapeValue: false}})}
                            <ArrowRight size={13} weight="bold"/>
                        </Link>
                    </div>
                </>
            )}
        </section>
    );
}

/**
 * La banda «Usa tu IA»: el cierre del Recorrido.
 *
 * Es de otra naturaleza que los tres pasos y por eso tiene otro fondo, otra luz y otra tipografía:
 * acá no hay nada que completar. Lo que muestra es qué ve el asistente de este cliente — el mismo
 * inventario que `IAContextMeter` (`available_ingredients` de la biblioteca de prompts), pedido
 * con `cache: true` para que las dos superficies compartan UN request en vez de hacer dos.
 *
 * Del MISMO payload salen el prompt sugerido, la lista de la agencia y el plegado de Dashbo: la
 * biblioteca entera es una lectura distinta de lo que la banda ya tenía en la mano.
 */
export function BoosterUseIaBand({clientId, clientName, connected = false}) {
    const {t} = useTranslation();
    // El inventario y los prompts salen del MISMO hook que alimenta los chips del HUD del acto
    // Alimenta (`useClientPromptLibrary`): un request compartido (`cache: true`) y una sola lectura
    // del payload, así la banda y el HUD no pueden decir cosas distintas del mismo cliente.
    // El nombre de la agencia sale del chrome (cacheado en localStorage, así que está disponible
    // desde el primer paint): es el de «Gestionar los prompts de <agencia>». No se pide por
    // separado — un GET más para un nombre que el marco ya tiene en la mano.
    const {chrome, clientEconomicsEnabled} = useUserModules();
    const {prompts: rawPrompts, available, missing, status, addPrompt} = useClientPromptLibrary(clientId,
        {economicsEnabled: clientEconomicsEnabled === true});
    const agencyName = (chrome && chrome.agency && chrome.agency.name) || '';

    // El texto que la biblioteca sirve viene CRUDO a propósito (`{cliente}`, `{período}`): ahí es
    // una plantilla que la agencia puede editar y aplicar a cualquier cliente. Acá deja de serlo —
    // la banda habla de ESTE cliente y de HOY, y lo que el usuario aprieta es «Copiar» para pegar
    // en su IA. Un «Para «{cliente}» en {período}...» en el portapapeles es el bug que reportó
    // Pablo (card J7eyNTzE).
    //
    // Se resuelve UNA vez y acá arriba, no en cada superficie: el prompt sugerido, las filas de la
    // agencia y las cards de Dashbo son tres lecturas de esta misma lista, y ninguna de las tres
    // tiene por qué saber que existen placeholders. La función es la MISMA que usa el Panel del
    // cliente 2.1 (`IaCard`) para su prompt de fallback — misma semántica, un solo lugar: `{canal}`
    // queda literal a propósito y un valor que no se pudo resolver deja el placeholder a la vista.
    //
    // Y lo que queda con un placeholder que ESTA pantalla no puede completar, no se ofrece. Son los
    // 9 prompts de la familia `alerta-*` del catálogo, que traen `{alerta}` y `{línea}`: los llena
    // el hecho que disparó la alerta, y el producto se los propone al usuario en el rail del Panel
    // con la alerta ya conocida. En esta grilla, que el usuario recorre a mano, no hay ninguna
    // alerta que los complete — y salen a los tres botones de copiar porque declaran
    // `required: []`, así que el filtro de ingredientes nunca los toca (el 09/09 estaban en la
    // grilla de los 53 prompts de la Demo, y copiarlos pegaba «…: {alerta} (línea de control:
    // {línea})»). Es PEOR que el bug original: `{cliente}` el usuario lo puede completar a mano,
    // `{alerta}` nombra un objeto interno de Dashbo que en el chat no tiene con qué llenar.
    //
    // El descarte va acá arriba y no en cada superficie para que los contadores de los filtros
    // cuenten lo que se ve. Y se decide por el TEXTO, no por el slug: un filtro por `alerta-*`
    // quedaría atado a la convención de nombres del catálogo y no cubriría un prompt que escriba
    // una agencia con esos tokens.
    //
    // ⚠️ El arreglo de fondo es que el endpoint no los sirva a una grilla —lo que el propio YAML
    // afirma que ya pasa— pero NO puede ir en `library_visible_for_client`: de ese mismo queryset
    // saca `MomentPromptService` los prompts de alerta del Panel, así que excluirlos ahí apagaría
    // el prompt del momento, que es justo donde estos prompts SÍ sirven. Iría en
    // `library_for_client`, y ahí es decisión de producto (los esconde también de la biblioteca
    // clásica). Mientras tanto, esta pantalla no ofrece lo que no puede cumplir.
    //
    // `needs_client` se calcula sobre el texto CRUDO, antes de resolverlo: es lo que decide si
    // «Copiar pedido» fija este cliente (`askPinsClient`). Un prompt de cartera no lo trae.
    const prompts = useMemo(() => rawPrompts
        .filter((prompt) => !hasUnresolvablePlaceholders(prompt.prompt_text))
        .map((prompt) => ({
            ...prompt,
            needs_client: textNeedsClient(prompt.prompt_text),
            prompt_text: resolvePromptPlaceholders(prompt.prompt_text, {clientName}),
        })), [rawPrompts, clientName]);

    const client = clientName || t('ia_booster.this_client');
    const label = (ing) => t(`prompt_library.ingredients.${ing}`, ing);
    // Con el margen cargado, los prompts de rentabilidad van en su bloque y salen de la biblioteca.
    const {profitability, rest} = useMemo(() => splitProfitability(prompts, available), [prompts, available]);
    const library = useMemo(() => splitBandLibrary(rest), [rest]);
    const collapse = useStableCollapse();

    return (
        <div className="iab-band">
            <span className="iab-band-node" aria-hidden="true">
                <Lightning size={22} weight="fill"/>
            </span>
            <div className="iab-band-inner">
                <span className="iab-band-kicker">
                    <Lightning size={14} weight="fill"/>
                    {t('sidebar_v2.booster_use_ia')}
                    <span className="iab-band-tag">{t('ia_booster.band.tag')}</span>
                </span>

                {/* El título va partido en dos claves en vez de llevar HTML adentro: i18next
                    escapa los valores interpolados por default (`escapeValue: true` en `i18n.js`),
                    así que un `<em>` metido en la traducción se imprimiría como texto. */}
                <h2 className="iab-band-title">
                    {t('ia_booster.band.title_before')}{' '}
                    <em>{t('ia_booster.band.title_em')}</em>
                </h2>
                <p className="iab-band-sub">
                    {t('ia_booster.band.sub', {client, interpolation: {escapeValue: false}})}
                </p>

                <div className={`iab-conn iab-rv ${connected ? '' : '-off'}`}>
                    <span className="iab-conn-icon">
                        {connected ? <Check size={18} weight="bold"/> : <Warning size={18} weight="bold"/>}
                    </span>
                    <div className="iab-conn-body">
                        <h3 className="iab-conn-title">
                            {t(connected ? 'ia_booster.band.connected_title' : 'ia_booster.band.disconnected_title',
                                {client, interpolation: {escapeValue: false}})}
                        </h3>
                        <p className="iab-conn-text">
                            {t(connected ? 'ia_booster.band.connected_text' : 'ia_booster.band.disconnected_text')}
                        </p>
                        {/* `available === null` = todavía no llegó (o falló). No se dibuja una
                            lista vacía: diría que el asistente no ve nada de este cliente, que es
                            una afirmación distinta de "no lo pude averiguar". */}
                        {available !== null && (
                            <div className="iab-sees">
                                {available.map(ing => (
                                    <span key={ing} className="iab-see">
                                        <Check size={11} weight="bold"/> {label(ing)}
                                    </span>
                                ))}
                                {/* El margen faltante es el único que se resuelve en una pantalla
                                    propia de Dashbo (cargar la economía): el chip lleva ahí. */}
                                {missing.map(ing => (ing === ING_MARGEN ? (
                                    <Link key={ing} className="iab-see -off -link" to={connectPathFor(ing, clientId)}
                                          aria-label={t('client_panel.ia.unlock.margen_missing')}
                                          data-testid="iab-see-margen">
                                        {label(ing)}
                                        <ArrowRight size={11} weight="bold"/>
                                    </Link>
                                ) : (
                                    <span key={ing} className="iab-see -off">{label(ing)}</span>
                                )))}
                            </div>
                        )}
                    </div>
                    {/* Conectar OTRA IA no es un feature nuevo: manda a la pantalla de conexión que
                        ya existe, `/dashbo-mcp` (la key del conector es de la AGENCIA, así que sumar
                        Claude al lado de ChatGPT se resuelve ahí, no por cliente).
                        ⚠️ NO apunta a `/client/:id/ia/connect`: para una agencia con `ficha21` —o
                        sea, exactamente las que ven esta banda— esa ruta redirige a
                        `/client/:id/booster#usa` (`ClientIATabContent.render` → `boosterFlowPathForView`),
                        así que el botón sería un rulo que devuelve al usuario acá mismo. */}
                    <Link className="iab-btn -ghost iab-conn-cta" to="/dashbo-mcp">
                        <Plugs size={14}/> {t('ia_booster.band.connect_other')}
                    </Link>
                </div>

                <PromptPanelCard prompt={library.suggested} clientId={clientId} clientName={clientName}
                                 connected={connected}/>

                {/* «Rentabilidad»: los prompts que usan el margen cargado en Dashbo. Sólo existe
                    cuando el cliente lo tiene (`margen` disponible). */}
                {profitability.length > 0 && (
                    <section className="iab-profit iab-rv" data-testid="iab-profitability"
                             aria-labelledby="iab-profit-title">
                        <h3 className="iab-lib-title" id="iab-profit-title">
                            {t('booster.use_ia.profitability.title')}
                        </h3>
                        <p className="iab-lib-sub">{t('booster.use_ia.profitability.hint')}</p>
                        <div className="iab-jgrid">
                            {profitability.map((prompt) => (
                                <PromptMiniCard key={prompt.id} prompt={prompt} clientId={clientId}/>
                            ))}
                        </div>
                    </section>
                )}

                <UseIaLibrary clientId={clientId} clientName={clientName} library={library}
                              total={prompts.length} status={status} agencyName={agencyName}
                              onPromptCreated={addPrompt} collapse={collapse}/>

                <div className="iab-cycle iab-rv">
                    <span className="iab-cycle-icon"><ArrowsClockwise size={16}/></span>
                    <p className="iab-cycle-text">
                        <b>{t('ia_booster.band.cycle_title')}</b> {t('ia_booster.band.cycle_text')}
                    </p>
                    <Link className="iab-btn -ghost" to={boosterPathFor(BOOSTER_STEP_FEED, clientId)}>
                        {t('ia_booster.band.cycle_cta')}
                    </Link>
                </div>

                {/* El hueco que reserva el plegado de Dashbo va al FINAL de la banda —lo último de
                    la página—, nunca entre dos bloques: se ve sólo si se baja hasta el fondo. */}
                {collapse.reserve > 0 && (
                    <div className="iab-collapse-reserve" ref={collapse.reserveRef} aria-hidden="true"
                         style={{height: collapse.reserve}}/>
                )}
            </div>
        </div>
    );
}

export default BoosterUseIaBand;
